import { Fragment, ReactNode } from "react";
import { TooltipPortal } from "@radix-ui/react-tooltip";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/Tooltip";
import type { IntentHighlightAnnotation } from "@/types/requests";
import {
  findHighlightAnnotation,
  formatIntentHighlightTooltip,
  mergeHighlightedPreviewSegments,
} from "@/utils/renderHighlightedPreviewUtils";

export {
  findHighlightAnnotation,
  formatIntentHighlightTooltip,
  isMergeableHighlightGap,
  mergeHighlightedPreviewSegments,
} from "@/utils/renderHighlightedPreviewUtils";

const HIGHLIGHT_CLASS_NAME =
  "rounded-sm bg-blue-100 px-0.5 font-medium text-blue-800 not-italic no-underline cursor-help";

const HIGHLIGHT_TRIGGER_CLASS_NAME = "inline-block w-fit max-w-full align-baseline";

const HighlightWithTooltip = ({ content, tooltip }: { content: string; tooltip: string }) => (
  <Tooltip delayDuration={0}>
    <TooltipTrigger asChild>
      <span className={HIGHLIGHT_TRIGGER_CLASS_NAME}>
        <mark className={HIGHLIGHT_CLASS_NAME}>{content}</mark>
      </span>
    </TooltipTrigger>
    <TooltipPortal>
      <TooltipContent side="top" variant="black">
        {tooltip}
      </TooltipContent>
    </TooltipPortal>
  </Tooltip>
);

export const renderHighlightedPreview = (
  preview: string,
  annotations?: IntentHighlightAnnotation[] | null,
): ReactNode => {
  if (!/<em>/i.test(preview)) {
    return preview;
  }

  const segments = mergeHighlightedPreviewSegments(preview);
  const parts: ReactNode[] = [];
  let key = 0;

  for (const segment of segments) {
    if (segment.kind === "text") {
      parts.push(<Fragment key={key}>{segment.content}</Fragment>);
    } else {
      const annotation = findHighlightAnnotation(segment.content, annotations);
      parts.push(
        annotation ? (
          <HighlightWithTooltip
            key={key}
            content={segment.content}
            tooltip={formatIntentHighlightTooltip(annotation)}
          />
        ) : (
          <mark key={key} className={HIGHLIGHT_CLASS_NAME}>
            {segment.content}
          </mark>
        ),
      );
    }
    key += 1;
  }

  if (parts.length === 0) {
    return preview;
  }

  return <TooltipProvider delayDuration={0}>{parts}</TooltipProvider>;
};
